import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  InputSignal,
  signal,
  Type,
} from '@angular/core';
import { ModalBase } from '@shared/abstraction/modal/modal-base';
import { ModalFactoryOptions } from '@shared/abstraction/modal/modal-factory-options';
import { GlobalDialogComponent } from '@shared/components/global-dialog/global-dialog.component';
import { ModalRef } from '@shared/models/modal-ref';

@Injectable({
  providedIn: 'root',
})
export class DialogFactoryService {
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly applicationRef = inject(ApplicationRef);
  private readonly document = inject(DOCUMENT);

  open<T>(
    title: string,
    componentType: Type<T>,
    options: ModalFactoryOptions
  ): ModalRef<T> {
    const modalHostElement = this.document.createElement('div');
    this.document.body.appendChild(modalHostElement);

    const modalWrapperRef = createComponent(GlobalDialogComponent, {
      environmentInjector: this.environmentInjector,
      hostElement: modalHostElement,
    });

    if (options) {
      if (options.cancelLabel) {
        modalWrapperRef.instance.cancelLabel = signal<string>(
          options.cancelLabel
        ) as unknown as InputSignal<string>;
      }

      if (options.confirmLabel) {
        modalWrapperRef.instance.confirmLabel = signal<string>(
          options.confirmLabel
        ) as unknown as InputSignal<string>;
      }

      if (options.hideCancel) {
        modalWrapperRef.instance.hideCancel = signal<boolean>(
          options.hideCancel
        ) as unknown as InputSignal<boolean>;
      }

      if (options.hideConfirm) {
        modalWrapperRef.instance.hideConfirm = signal<boolean>(
          options.hideConfirm
        ) as unknown as InputSignal<boolean>;
      }

      if (options.disableConfirm) {
        modalWrapperRef.instance.disableConfirm = signal<boolean>(
          options.disableConfirm
        ) as unknown as InputSignal<boolean>;
      }
    }

    if (title) {
      modalWrapperRef.instance.title = signal<string>(
        title
      ) as unknown as InputSignal<string>;
    }

    this.applicationRef.attachView(modalWrapperRef.hostView);

    const componentRef = modalWrapperRef.instance
      .dynamicContentHost()!
      .createComponent(componentType, {
        environmentInjector: this.environmentInjector,
      });

    (componentRef.instance as unknown as ModalBase).data = signal(options.data);

    const modalRef = new ModalRef(
      componentRef,
      modalWrapperRef,
      modalHostElement
    );

    modalWrapperRef.instance.cancel.subscribe((result?: boolean) => {
      modalRef.close(result);
    });

    modalWrapperRef.instance.confirm.subscribe((result?: boolean) => {
      const component = componentRef.instance as ModalBase;
      if (component.getData) {
        const data = component.getData();
        modalRef.confirm(data);
      }
      modalRef.close(result);
    });

    return modalRef;
  }
}
